import { NavigationError, Router, RouterModule, RouterOutlet } from '@angular/router';
import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RelativeLoaderDirective } from './shared/directives/relative-loader/relative-loader.directive';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { LanguageType } from './shared/constants/language-eum';
import { LoaderService } from './shared/services/loader/loader.service';
import { TitleService } from './shared/services/title/title.service';
import { TokenRefreshService } from './shared/services/auth/token-refresh.service';
import { SESSION_TIMEOUT } from './shared/constants/system-constants.model';
import { LOCAL_STORAGE } from './shared/constants/localstorage-enum';
import { delay, filter, Subject, switchMap, take, takeUntil } from 'rxjs';
import { LanguageService } from './shared/services/translation/language.service';
import { AuthService } from './shared/services/auth/auth.service';
import { SignalrService } from './shared/services/common/signalR.service';
import { UserType } from './shared/constants/user-type-enum';
import { Store } from '@ngxs/store';
import { AuthState } from './shared/store/auth/auth.state';
import * as L from 'leaflet';
import { ToastrHelperService } from './shared/services/common/toast-helper.service';
import { PwaInstallService } from './shared/services/pwa-install/pwa-install.service';
import { AsyncPipe, NgIf } from '@angular/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterModule, TranslateModule, LoaderComponent, RelativeLoaderDirective, NgIf, AsyncPipe],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  private loaderService = inject(LoaderService);
  private titleService = inject(TitleService);
  private tokenRefreshService = inject(TokenRefreshService);
  private languageService = inject(LanguageService);
  private authService = inject(AuthService);
  private signalRService = inject(SignalrService);
  private store = inject(Store);
  private zone = inject(NgZone);
  private toastr = inject(ToastrHelperService);
  private router = inject(Router);
  pwaInstallService = inject(PwaInstallService);

  title = 'TPA';
  onDestroy$: Subject<void> = new Subject<void>();
  userType: UserType;

  constructor() {
    this.loaderService.show();
    const currentLanguage = (localStorage.getItem(LOCAL_STORAGE.CURRENT_LANGUAGE) ? <LanguageType>localStorage.getItem(LOCAL_STORAGE.CURRENT_LANGUAGE) : LanguageType.English).toString();
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', currentLanguage);
    this.titleService.setTitle();

    // Leaflet workaround: delete private _getIconUrl to fix marker icon paths in Angular
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
      iconUrl: 'assets/leaflet/marker-icon.png',
      shadowUrl: 'assets/leaflet/marker-shadow.png',
    });
  }

  ngOnInit(): void {
    window.addEventListener('offline', () => {
      this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe(event => {
        if (event instanceof NavigationError) {
          if (event.error?.name === 'ChunkLoadError') {
            this.zone.run(() => {
              this.loaderService.hide();
              this.toastr.showErrorMessage('COMMON.NO_INTERNET_ROUTE_CHANGE');
            });
          }
        }
      });
    });

    this.userType = this.store.selectSnapshot(AuthState.userType);

    // watch to display session timeout dialog
    this.languageService.languageReady$.pipe(
      filter(ready => ready), take(1),
      switchMap(() => this.tokenRefreshService.sessionExpiring$.pipe(takeUntil(this.onDestroy$)))
    ).subscribe((allow: boolean) => allow && this.tokenRefreshService.displaySessionTimeoutDialog());

    //reload page after getting different language preference from user setting
    this.languageService.reloadWindow$.pipe(delay(100), takeUntil(this.onDestroy$)).subscribe((shouldReload: boolean) => {
      if (shouldReload) {
        if (this.userType === UserType.Patient)
          window.location.reload();
        else
          this.languageService.displayConfirmLanguageDialog(true);
      }
    });

    // watch for token time expired without any user action
    this.tokenRefreshService.sessionExpired$.pipe(takeUntil(this.onDestroy$)).subscribe(
      (allow: boolean) => allow && this.tokenRefreshService.setSessionTimeout(SESSION_TIMEOUT.LOGOUT, true)
    );

    // start new time from here on page hard reload
    this.tokenRefreshService.resetTimer();
    if (this.authService.isLoggedIn())
      this.signalRService.startConnection();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.signalRService.stopConnection();
  }
}
